if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.sectionId}`);
        this.customFields = document.querySelectorAll(`[id^='CustomField-${this.dataset.sectionId}-']`);
        this.variantInputs = this.form.querySelector(".product-form__variants");
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const clickedButton = evt.submitter || this.submitButton;
        this.activeSubmitButton = clickedButton;
        this.activeSubmitButtonText = clickedButton.querySelector('span') || this.submitButtonText;

        if (clickedButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        clickedButton.setAttribute('aria-disabled', true);
        clickedButton.classList.add('loading');
        if(clickedButton.querySelector('.loading__spinner')){
          clickedButton.querySelector('.loading__spinner').classList.remove('hidden');
        }

        this.skipCart = clickedButton.dataset.skipCart === "true";

        // "Buy it now" must isolate checkout to just the clicked item without losing whatever
        // was already in the customer's cart. Stash the existing cart contents (if any) and
        // clear the cart before adding the single clicked item, so checkout only shows that
        // item. layout/theme.liquid restores the stashed cart on the customer's next page load
        // (after they complete or abandon that single-item checkout).
        const preStep = this.skipCart ? this.stashCartForBuyNow() : Promise.resolve();

        preStep
          .catch((error) => {
            console.error('Buy it now: failed to stash existing cart, continuing without stashing', error);
          })
          .then(() => {
            this.submitAddToCart(clickedButton);
          });
      }

      stashCartForBuyNow() {
        return fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            if (cart && Array.isArray(cart.items) && cart.items.length > 0) {
              const stash = {
                items: cart.items.map((item) => ({ id: item.variant_id, quantity: item.quantity })),
                note: cart.note || '',
                savedAt: Date.now(),
              };
              try {
                window.localStorage.setItem('wanderstore_buy_now_stashed_cart', JSON.stringify(stash));
              } catch (error) {
                console.error('Buy it now: failed to save stashed cart to localStorage', error);
              }
              return fetch(`${window.routes.cart_url}/clear.js`, { method: 'POST' });
            }

            try {
              window.localStorage.removeItem('wanderstore_buy_now_stashed_cart');
            } catch (error) {
              // ignore
            }
            return Promise.resolve();
          });
      }

      submitAddToCart(clickedButton) {
        let hasVariants = false;
        if (this.quantityBreaks && this.variantInputs && this.quantityBreaks.formVariants?.length > 0) {
          let quantityInputs = "";
          hasVariants = true;
          for (let i = 0; i < this.quantityBreaks.formVariants.length; i++) {
              let variant = this.quantityBreaks.formVariants[i];
              quantityInputs += `<input type="hidden" name="items[${i}][quantity]" value="${variant.quantity}">`;
              quantityInputs += `<input type="hidden" name="items[${i}][id]" value="${variant.id}">`;
              this.customFields.forEach(field => {
                  quantityInputs += `<input type="hidden" name="items[${i}][properties][${field.fieldName}]" value="${field.value}">`;
              });
          }
          this.variantInputs.innerHTML = quantityInputs;
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (hasVariants) {
          let options = this.dataset.options ? this.dataset.options.split(",") : [];
          let fieldsToDelete = ["id", "quantity", ...options];
          for (let field of fieldsToDelete) {
              formData.delete(field);
          }
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = clickedButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              clickedButton.setAttribute('aria-disabled', true);
              this.activeSubmitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }else if (this.skipCart) {
                  window.location = "/checkout";
                  return;
              }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            clickedButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) clickedButton.removeAttribute('aria-disabled');
            if (clickedButton.querySelector('.loading__spinner')){
              clickedButton.querySelector('.loading__spinner').classList.add('hidden');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
